if (!customElements.get('ritual-subscription-selector')) {
  customElements.define(
    'ritual-subscription-selector',
    class RitualSubscriptionSelector extends HTMLElement {
      constructor() {
        super();
        this.purchaseInputs = this.querySelectorAll('[data-ritual-purchase-option]');
        this.planInputs = this.querySelectorAll('[data-ritual-plan-option]');
        this.sellingPlanInput = this.querySelector('[data-ritual-selling-plan-input]');
        this.subscriptionPrice = this.querySelector('[data-ritual-subscription-price]');
        this.subscriptionSavings = this.querySelector('[data-ritual-subscription-savings]');
        this.oneTimePrice = this.querySelector('[data-ritual-one-time-price]');
        this.dataScript = this.querySelector('[data-ritual-subscription-data]');
        this.data = this.parseData();
        this.variantChangeUnsubscriber = undefined;
        this.onFaqPromoButtonClick = this.onFaqPromoButtonClick.bind(this);
      }

      connectedCallback() {
        this.purchaseInputs.forEach((input) => input.addEventListener('change', this.onPurchaseOptionChange.bind(this)));
        this.planInputs.forEach((input) => input.addEventListener('change', this.onPlanChange.bind(this)));
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        document.addEventListener('click', this.onFaqPromoButtonClick);
        this.syncSelectedClasses();
        this.updateSellingPlanInput();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        document.removeEventListener('click', this.onFaqPromoButtonClick);
      }

      parseData() {
        if (!this.dataScript) return { variants: {}, labels: {} };

        try {
          return JSON.parse(this.dataScript.textContent);
        } catch (error) {
          console.error('Failed to parse ritual subscription data', error);
          return { variants: {}, labels: {} };
        }
      }

      onPurchaseOptionChange() {
        this.syncSelectedClasses();
        this.updateSellingPlanInput();
      }

      onPlanChange(event) {
        const subscriptionInput = this.querySelector('[data-ritual-purchase-option][value="subscription"]');
        if (subscriptionInput) subscriptionInput.checked = true;

        this.syncSelectedClasses();
        this.updateSellingPlanInput(event.currentTarget.value);
        this.updateSubscriptionPrice(event.currentTarget.value);
      }

      onVariantChange(event) {
        const sectionId = event?.data?.sectionId;
        if (sectionId && sectionId !== this.dataset.sectionId) return;

        const variant = event?.data?.variant;
        if (!variant) return;

        this.updateForVariant(variant.id);
      }

      onFaqPromoButtonClick(event) {
        const button = event.target.closest('.faq__promo-button');
        if (!button) return;

        event.preventDefault();
        this.selectSubscriptionOption();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      selectSubscriptionOption() {
        const subscriptionInput = this.querySelector('[data-ritual-purchase-option][value="subscription"]');
        if (!subscriptionInput) return;

        subscriptionInput.checked = true;
        this.syncSelectedClasses();
        this.updateSellingPlanInput();
      }

      getSelectedPurchaseOption() {
        return this.querySelector('[data-ritual-purchase-option]:checked')?.value || 'subscription';
      }

      getSelectedPlanInput() {
        return this.querySelector('[data-ritual-plan-option]:checked') || this.querySelector('[data-ritual-plan-option]:not(:disabled)');
      }

      syncSelectedClasses() {
        this.classList.toggle('is-one-time-selected', this.getSelectedPurchaseOption() === 'one-time');

        this.querySelectorAll('.ritual-subscription-selector__purchase-option').forEach((label) => {
          const input = label.querySelector('[data-ritual-purchase-option]');
          label.classList.toggle('is-selected', Boolean(input?.checked));
        });

        this.querySelectorAll('.ritual-subscription-selector__plan').forEach((label) => {
          const input = label.querySelector('[data-ritual-plan-option]');
          label.classList.toggle('is-selected', Boolean(input?.checked));
        });
      }

      updateSellingPlanInput(preferredPlanId = null) {
        if (!this.sellingPlanInput) return;

        if (this.getSelectedPurchaseOption() !== 'subscription') {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
          return;
        }

        const selectedPlan = preferredPlanId
          ? this.querySelector(`[data-ritual-plan-option][value="${CSS.escape(preferredPlanId)}"]`)
          : this.getSelectedPlanInput();

        this.sellingPlanInput.disabled = !selectedPlan?.value;
        this.sellingPlanInput.value = selectedPlan?.value || '';
      }

      updateSubscriptionPrice(planId) {
        const variantId = this.getCurrentVariantId();
        const allocation = this.data.variants?.[variantId]?.allocations?.[planId];
        if (!allocation) return;

        if (this.subscriptionPrice) this.subscriptionPrice.textContent = allocation.formattedPrice;
        if (this.subscriptionSavings) {
          this.subscriptionSavings.textContent = allocation.formattedSavings
            ? `(${this.data.labels.savingsPrefix} ${allocation.formattedSavings})`
            : '';
        }
      }

      updateForVariant(variantId) {
        const variantData = this.data.variants?.[variantId];
        if (!variantData) return;

        if (this.oneTimePrice) this.oneTimePrice.textContent = variantData.formattedPrice;

        let selectedPlanIsAvailable = false;
        const selectedPlanId = this.getSelectedPlanInput()?.value;
        let firstAvailablePlan = null;

        this.planInputs.forEach((input) => {
          const allocation = variantData.allocations?.[input.value];
          const planLabel = input.closest('.ritual-subscription-selector__plan');
          const savingsEl = this.querySelector(`[data-ritual-plan-savings="${CSS.escape(input.value)}"]`);
          const isAvailable = Boolean(allocation);

          input.disabled = !isAvailable;
          if (planLabel) planLabel.classList.toggle('is-disabled', !isAvailable);

          if (allocation && savingsEl) {
            savingsEl.textContent = allocation.formattedSavings
              ? `${this.data.labels.planSavingsPrefix} ${allocation.formattedSavings}`
              : '';
          }

          if (isAvailable && !firstAvailablePlan) firstAvailablePlan = input;
          if (isAvailable && input.value === selectedPlanId) selectedPlanIsAvailable = true;
        });

        if (!selectedPlanIsAvailable && firstAvailablePlan) {
          firstAvailablePlan.checked = true;
        }

        const selectedPlan = this.getSelectedPlanInput();
        if (selectedPlan) this.updateSubscriptionPrice(selectedPlan.value);

        this.syncSelectedClasses();
        this.updateSellingPlanInput();
      }

      getCurrentVariantId() {
        const form = document.getElementById(this.dataset.productFormId);
        const input = form?.querySelector('[name="id"]');
        return input?.value;
      }
    },
  );
}
